import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Client } from '../../../data/models/client.model';
import PurposeCodeList from './PurposeCodeList';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ClientPurposeCodes';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.subheader}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface ClientPurposeCodesProps {
  client?: Client;
  purposeCodes: ClientPurposeCode[];
  onAdd: () => void;
  onActivate: (purposeCode: ClientPurposeCode) => void;
  onDeactivate: (purposeCode: ClientPurposeCode) => void;
  onDelete: (purposeCode: ClientPurposeCode) => void;
  onStartDrag: (purposeCode: ClientPurposeCode) => void;
  onEndDrag: (purposeCode: ClientPurposeCode) => void;
}

export default function ClientPurposeCodes({
  client,
  purposeCodes,
  onAdd,
  onActivate,
  onDeactivate,
  onDelete,
  onStartDrag,
  onEndDrag,
}: ClientPurposeCodesProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.purposeCodes')}
          </Typography>
          {!isSuperReaderUser && (
            <Tooltip title={t('client.addPurposeCode')}>
              <span>
                <IconButton
                  data-testid="add-purpose-code"
                  onClick={onAdd}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box className={classes.subheader}>{t('client.dragPurposeCode')}</Box>
        {client && (
          <PurposeCodeList
            client={client}
            purposeCodes={purposeCodes}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onDelete={onDelete}
            onStartDrag={onStartDrag}
            onEndDrag={onEndDrag}
          />
        )}
      </Paper>
    </StyledBox>
  );
}
